import { apiClient } from '../api/apiClient';
import { tokenService } from '../storage/tokenService';
import { API_ENDPOINTS, ERROR_MESSAGES } from '../../utils/constants';
import type { LoginCredentials, AuthResponse, AuthTokens, UserInfo } from '../../types/auth';

/**
 * Authentication Service
 * Handles all authentication-related operations
 */
export const authService = {
  /**
   * Login user with credentials
   * @param credentials - Username and password
   * @returns Promise with authentication response including tokens and user info
   * @throws Error if login fails
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>(
        API_ENDPOINTS.AUTH.LOGIN,
        credentials
      );

      const { access, refresh, user } = response.data;

      // Store tokens and user info
      tokenService.setTokens({ access, refresh });
      tokenService.setUserInfo(user);

      return response.data;
    } catch (error: any) {
      // Handle specific error cases
      if (error.status === 401 || error.originalError?.response?.status === 401) {
        throw new Error(ERROR_MESSAGES.INVALID_CREDENTIALS);
      }

      if (error.message === ERROR_MESSAGES.NETWORK_ERROR) {
        throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
      }

      if (error.status >= 500 || error.message === ERROR_MESSAGES.SERVER_ERROR) {
        throw new Error(ERROR_MESSAGES.SERVER_ERROR);
      }

      // Log error for debugging
      console.error('Login error:', error);
      
      // Throw generic error message
      throw new Error(error.message || 'Login failed. Please try again.');
    }
  },

  /**
   * Logout user
   * Calls backend logout endpoint to blacklist refresh token and clears local storage
   * @returns Promise that resolves when logout is complete
   */
  async logout(): Promise<void> {
    try {
      const refreshToken = tokenService.getRefreshToken();

      if (refreshToken) {
        // Call backend logout endpoint to blacklist the refresh token
        await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT, {
          refresh: refreshToken,
        });
      }
    } catch (error: any) {
      // Log error but don't throw - we still want to clear local tokens
      console.error('Logout error:', error);
    } finally {
      // Always clear tokens from local storage
      tokenService.clearTokens();
    }
  },

  /**
   * Refresh access token using refresh token
   * @returns Promise with new tokens
   * @throws Error if refresh fails
   */
  async refreshToken(): Promise<AuthTokens> {
    try {
      const refreshToken = tokenService.getRefreshToken();

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiClient.post<{ access: string; refresh?: string }>(
        API_ENDPOINTS.AUTH.REFRESH,
        { refresh: refreshToken }
      );

      // With ROTATE_REFRESH_TOKENS=True the backend returns a new refresh
      // token and blacklists the old one. We must persist whichever one
      // came back, falling back to the original only if rotation is off.
      const newTokens: AuthTokens = {
        access: response.data.access,
        refresh: response.data.refresh ?? refreshToken,
      };

      // Store new tokens
      tokenService.setTokens(newTokens);

      return newTokens;
    } catch (error: any) {
      // Clear tokens if refresh fails
      tokenService.clearTokens();

      // Log error for debugging
      console.error('Token refresh error:', error);

      throw new Error(ERROR_MESSAGES.TOKEN_EXPIRED);
    }
  },

  /**
   * Get current user information
   * Fetches user data from backend using stored access token
   * @returns Promise with user information
   * @throws Error if request fails
   */
  async getMe(): Promise<UserInfo> {
    try {
      const response = await apiClient.get<UserInfo>(API_ENDPOINTS.AUTH.ME);

      // Update stored user info
      tokenService.setUserInfo(response.data);

      return response.data;
    } catch (error: any) {
      // Log error for debugging
      console.error('Get user info error:', error);

      // Handle specific error cases
      if (error.status === 401 || error.originalError?.response?.status === 401) {
        tokenService.clearTokens();
        throw new Error(ERROR_MESSAGES.TOKEN_EXPIRED);
      }

      if (error.message === ERROR_MESSAGES.NETWORK_ERROR) {
        throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
      }

      if (error.status >= 500 || error.message === ERROR_MESSAGES.SERVER_ERROR) {
        throw new Error(ERROR_MESSAGES.SERVER_ERROR);
      }

      throw new Error(error.message || 'Failed to fetch user information');
    }
  },

  /**
   * Check if user is currently authenticated
   * @returns True if user has valid tokens, false otherwise
   */
  isAuthenticated(): boolean {
    return tokenService.isAuthenticated();
  },

  /**
   * Get cached user information from storage
   * @returns User info from localStorage or null if not found
   */
  getCachedUserInfo(): UserInfo | null {
    return tokenService.getUserInfo();
  },
};

export default authService;
